import { Group, Select, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch, IconUserPlus } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NovaOseba } from '../osebe/NovaOseba';
import { imeOsebe, vrsta as poKljucu } from '../osebe/vrste';

type Oseba = Shema['OsebaPodatki'];

const oznaka = (o: Oseba) => [imeOsebe(o) || `#${o.id}`, o.davcna].filter(Boolean).join(' · ');

/** Vrednost možnosti »Nov kupec« (id-ji oseb so številke). */
const NOVA = 'nova';

/**
 * Izbira osebe z iskanjem na strežniku: prvih 20
 * zadetkov po imenu, davčni, naslovu …; trenutno izbrana oseba ostane med možnostmi. Prva možnost je vedno
 * »Nov kupec« (prodajalec, uporabnik vozila) z vpisanim besedilom: odpre {@link NovaOseba} in novo osebo izbere.
 * Z {@code dodaj} jo zaslon obravnava sam (npr. nalog: obrazec nove
 * stranke). Izbira je samo na zaslonih za urejanje, katerih pravica dovoli tudi dodajanje osebe (strežnik:
 * {@code POST /api/osebe/{vrsta}/nova} z urejanjem računov, predračunov, vozil, delavnice ali strank).
 */
export function IzbiraOsebe({
  vrsta,
  oznaka: naslov,
  izbran,
  izberi,
  napaka,
  obvezno = true,
  dodaj,
}: {
  vrsta: 'prodajalci' | 'uporabniki-vozil' | 'kupci';
  oznaka: string;
  izbran?: Oseba | null;
  izberi: (o: Oseba | null) => void;
  napaka?: string;
  obvezno?: boolean;
  dodaj?: (iskanje: string) => void;
}) {
  const [iskanje, setIskanje] = useState('');
  const [zakasnjeno] = useDebouncedValue(iskanje, 300);
  const [nova, setNova] = useState<string | null>(null);
  const zadetki = useQuery({
    queryKey: ['izbira-osebe', vrsta, zakasnjeno],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/osebe/{vrsta}/pregled', {
          signal,
          params: { path: { vrsta }, query: { iskanje: zakasnjeno || undefined, velikost: 20 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const osebe = new Map<string, Oseba>();
  if (izbran?.id) {
    osebe.set(String(izbran.id), izbran);
  }
  (zadetki.data?.vsebina ?? []).forEach((o) => osebe.set(String(o.id), o));

  // iskalno besedilo, ko je oseba izbrana, je njena oznaka – za novo osebo ne velja
  const vpisano = izbran?.id && iskanje === oznaka(izbran) ? '' : iskanje.trim();
  const naslovNove = poKljucu(vrsta).nova + (vpisano ? ` »${vpisano}«` : '');
  const moznosti = [...osebe.entries()].map(([value, o]) => ({ value, label: oznaka(o) }));
  moznosti.unshift({ value: NOVA, label: naslovNove });

  return (
    <>
      <Select
        label={naslov}
        placeholder="Poišči …"
        withAsterisk={obvezno}
        searchable
        clearable
        leftSection={<IconSearch size={16} />}
        data={moznosti}
        value={izbran?.id ? String(izbran.id) : null}
        onChange={(v) => {
          if (v === NOVA) {
            if (dodaj) {
              dodaj(vpisano);
            } else {
              setNova(vpisano);
            }
            return;
          }
          izberi(v ? (osebe.get(v) ?? null) : null);
        }}
        searchValue={iskanje}
        onSearchChange={setIskanje}
        filter={({ options }) => options}
        renderOption={({ option }) =>
          option.value === NOVA ? (
            <Group gap={6} wrap="nowrap" c="var(--mantine-primary-color-filled)">
              <IconUserPlus size={16} />
              <Text size="sm" fw={600}>
                {option.label}
              </Text>
            </Group>
          ) : (
            option.label
          )
        }
        nothingFoundMessage={zadetki.isFetching ? 'Iščem …' : 'Ni zadetkov'}
        error={napaka}
      />
      {!dodaj && (
        <NovaOseba
          vrsta={vrsta}
          odprto={nova !== null}
          iskanje={nova ?? ''}
          zapri={() => setNova(null)}
          dodana={(o) => {
            setIskanje(oznaka(o));
            izberi(o);
          }}
        />
      )}
    </>
  );
}
